<template>
  <section id="entry-index" class="layout-section -bg-shade -search-top">
    <p v-if="$wp.secondaryDataLoaded && !publicationsPage.length" class="layout-section__heading">Aucun résultat</p>

    <div v-else class="card-article">
      <RouterLink
        v-for="(publication, index) in publicationsPage"
        :key="getEntryKey(publication)"
        v-scroll:reveal="{ delay: (index % 3) * 75, offset: 0 }"
        :to="{ name: 'event-single', params: { eventSlug: publication.slug } }"
        class="card-article__item"
      >
        <section class="card-article__type">
          <template v-if="getIcon(publication)">
            <span class="tag -float -small">
              <SvgIcon :id="getIcon(publication)" />
              {{ getTag(publication) }}
            </span>
          </template>

          <div v-if="getIsUpcoming(publication)" class="tag -small -float -accent">À venir</div>
        </section>

        <div class="card-article__figure" :style="{ backgroundImage: `url('/images/brand/placeholder.png')` }">
          <figure class="card-article__image" :style="{ backgroundImage: `url('${getImage(publication)}')` }" />
        </div>

        <section class="card-article__content">
          <section class="card-article__uptitle">
            <div class="card-article__data -accent">
              <SvgIcon id="content-date" />
              <p>{{ getFormattedDate(publication) || 'Aucune date' }}</p>
            </div>
          </section>

          <h4 class="card-article__title">
            {{ publication.shortTitle || publication.title }}
          </h4>
        </section>
      </RouterLink>
    </div>

    <PagePickerQuery :total-pages="totalPages" />
  </section>
</template>

<script>
import moment from 'moment'
import { safeArray, normalizeForSearch } from '@/helpers/search-helpers'
import { eventTypeTagMap, eventTypeIconMap } from '@/helpers/type-maps'

const perPage = 18

export default {
  name: 'EventsIndexIndex',
  computed: {
    searchTerm() {
      return normalizeForSearch(this.$route.query.search || '')
    },
    currentKeywords() {
      if (!this.$route.query.keywords) return []
      return Array.isArray(this.$route.query.keywords) ? this.$route.query.keywords : [this.$route.query.keywords]
    },
    currentPage() {
      return this.$route.query.page ? Math.max(parseInt(this.$route.query.page) - 1, 0) : 0
    },
    currentEventType() {
      return this.$route.query.eventType || null
    },
    allPublications() {
      return this.$wp.eventPublications || []
    },
    filteredPublications() {
      const filteredPublications = !this.currentEventType
        ? this.allPublications
        : this.allPublications.filter(pub => pub.eventType === this.currentEventType)

      return this.filterKeywordMatches(filteredPublications)
    },
    matchedPublications() {
      if (!this.searchTerm) return this.filteredPublications

      const results = this.filteredPublications.filter(({ content, title, keywords = [], authors = [] }) => {
        const searchableContent = [
          normalizeForSearch(content),
          normalizeForSearch(title),
          normalizeForSearch(
            safeArray(keywords)
              .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].name)
              .join(' ')
          ),
          normalizeForSearch(
            safeArray(authors)
              .map(id => this.$wp.authorsById[id] && this.$wp.authorsById[id].name)
              .join(' ')
          )
        ]

        return searchableContent.join(' ').includes(this.searchTerm)
      })

      return results
    },
    publicationsPage() {
      return this.matchedPublications.slice(this.currentPage * perPage, (this.currentPage + 1) * perPage)
    },
    totalPages() {
      return Math.ceil(this.matchedPublications.length / perPage)
    }
  },
  methods: {
    getIsUpcoming(publication) {
      return publication && publication.date ? moment(publication.date).isAfter(moment()) : false
    },
    getTag(publication) {
      return eventTypeTagMap[publication.eventType] || null
    },
    getIcon(publication) {
      return eventTypeIconMap[publication.eventType] || null
    },
    getImage(publication) {
      if (publication.featuredImage) return publication.featuredImage
      if (!publication.images || !publication.images.length) return '/images/brand/placeholder.png'

      const [image1, image2 = null] = publication.images
      return image1 || image2
    },
    getFormattedDate(publication) {
      return publication && publication.date ? moment(publication.date).format('D MMMM YYYY') : null
    },
    getEntryKey(entry) {
      const key = entry.id || `${entry.slug}-${entry.type}`
      return `${key}-${JSON.stringify(this.$route.query)}`
    },
    filterKeywordMatches(entries) {
      if (!this.currentKeywords.length) return entries

      return entries.filter(entry => {
        if (!entry || !entry.keywords) return false

        return safeArray(entry.keywords)
          .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].slug)
          .filter(Boolean)
          .some(slug => this.currentKeywords.includes(slug))
      })
    }
  }
}
</script>
